import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../APIs/axios';

// This lives above routing: socket hints wake one bounded, authoritative unread
// request even when Contacts is not mounted. Opening the menu never marks read.
export default function useAdminChatInbox(token, session, legacySocket) {
  const [notification, setNotification] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [inboxError, setInboxError] = useState(false);
  const [counts, setCounts] = useState({});
  const refreshRef = useRef(() => Promise.resolve());
  useEffect(() => {
    setNotification([]); setUnreadCount(0); setInboxError(false); setCounts({});
  }, [token]);
  useEffect(() => {
    if (!token) { refreshRef.current = () => Promise.resolve(); return undefined; }
    let stopped = false; let flight; let pending = false; let timer;
    const refresh = () => {
      if (stopped || document.hidden) return Promise.resolve();
      if (flight) { pending = true; return flight; }
      flight = api('chat/admin/unread', 'GET', {}, {
        timeout: 15000, headers: { 'x-access-token': token },
      }).then(({ data }) => {
        if (stopped) return;
        if (!Array.isArray(data.conversations) || !Number.isFinite(Number(data.total_unread))) {
          throw new Error('Unread inbox unavailable');
        }
        setNotification(data.conversations);
        setUnreadCount(Number(data.total_unread));
        setCounts(data.unread_by_chat || Object.fromEntries(data.conversations.map((item) => [String(item.chat_id), Number(item.unread_count)])));
        setInboxError(false);
      }).catch(() => {
        // An outage must not clear unread messages or the user's login.
        if (!stopped) setInboxError(true);
      }).finally(() => {
        flight = null;
        if (!stopped && pending) { pending = false; refresh(); }
      });
      return flight;
    };
    refreshRef.current = refresh;
    const visible = () => { if (!document.hidden) refresh(); };
    window.addEventListener('chat-v2-inbox', refresh);
    window.addEventListener('focus', visible);
    document.addEventListener('visibilitychange', visible);
    if (legacySocket && !session) {
      legacySocket.on('message recieved', refresh);
      legacySocket.on('connect', refresh);
    }
    // v2 already reconciles the inbox once a minute, with no per-page timer.
    if (!session) {
      const tick = () => { refresh(); timer = setTimeout(tick, 60000); };
      timer = setTimeout(tick, 60000);
    }
    refresh();
    return () => {
      stopped = true; clearTimeout(timer);
      window.removeEventListener('chat-v2-inbox', refresh);
      window.removeEventListener('focus', visible);
      document.removeEventListener('visibilitychange', visible);
      if (legacySocket && !session) {
        legacySocket.off('message recieved', refresh);
        legacySocket.off('connect', refresh);
      }
    };
  }, [token, session, legacySocket]);
  const refreshInbox = useCallback(() => refreshRef.current(), []);
  const unreadByChat = useMemo(() => new Map(Object.entries(counts)), [counts]);
  return { notification, setNotification, unreadCount, unreadByChat, inboxError, refreshInbox };
}
